import React, { useState, useEffect } from 'react';
import compose from 'recompose/compose';
import { isEqual } from 'lodash';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../../store';
import {
  DebtCollectionCaseType,
  DebtorClaimStage,
  LeapClaim,
  DebtorIndex,
  SelectedDebtorClaim,
  SendPaymentInformationConversationAction,
} from '../../../../api/client';
import { actionCreators as ConversationSupervisorStoreActionCreators } from '../../../../store/ConversationSupervisorStore';
import { actionCreators as NotificationStoreActionCreators } from '../../../../store/NotificationsStore';
import { actionCreators as DebtorClaimsStoreActionCreators } from '../../../../store/DebtorClaimsStore';
import {
  getTopLevelLeapClaimsFromSelectedClaims,
  hasSelectedDebtorClaims,
} from '../../../../store/Selectors';
import { Step } from '../ConversationSupervisorStep';
import StepZilla from 'react-stepzilla';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import { MuiChipsInput } from 'mui-chips-input';
import { parsePhoneNumber } from 'awesome-phonenumber';
import currencyFormatter from 'currency-formatter';
import {
  formatDate,
  validateEmailAddress,
  formatPhoneNumber,
  validateCountryCode,
} from '../../../Utils';
import AddCircle from '@mui/icons-material/AddCircle';

interface PropsFromState {
  selectedDebtorClaims: SelectedDebtorClaim[];
  hasSelectedDebtorClaims: boolean;
  leapClaimsFromSelectedClaims: LeapClaim[];
  debtorIndex: DebtorIndex | null;
}

interface PropsFromDispatch {
  addConversationSupervisorAction: typeof ConversationSupervisorStoreActionCreators.addConversationAction;
  clearDebtorClaimsSelection: typeof DebtorClaimsStoreActionCreators.clearDebtorClaimsSelection;
  toggleDebtorClaimSelection: typeof DebtorClaimsStoreActionCreators.toggleDebtorClaimSelection;
  addNotification: typeof NotificationStoreActionCreators.addNotification;
}

interface InjectedProps {
  jumpToStep?: typeof StepZilla.jumpToStep;
}

type ComponentProps = PropsFromState & PropsFromDispatch & InjectedProps;

const SendSelectedInvoices = (props: ComponentProps) => {
  const {
    selectedDebtorClaims,
    hasSelectedDebtorClaims,
    leapClaimsFromSelectedClaims,
  } = props;

  const [availableMobileNumbers] = useState<string[]>(props.debtorIndex?.phoneNumbers?.map(x => formatPhoneNumber(x)).filter(x => x && x.length > 0).filter(Boolean) as string[]);
  const [availableEmailAddress] = useState<string | undefined>(props.debtorIndex?.email && validateEmailAddress(props.debtorIndex.email) ? props.debtorIndex.email : undefined);
  const [mobileNumbers, setMobileNumbers] = useState<string[]>([]);
  const [emailAddresses, setEmailAddresses] = useState<string[]>([]);
  const [leapClaimsFromSelection, setLeapClaimsFromSelection] = useState<LeapClaim[]>([]);
  const [introductionText, setIntroductionText] = useState(`Hei

Sender som avtalt betalingsinformasjon`);
  const [paymentInformationText, setPaymentInformationText] = useState('');
  
  const smsFooter = `Se og betal dine fakturaer på minside.credicare.no
Kan ikke besvares.
Hilsen CrediCare AS`;
  const emailFooter = `Se og betal dine fakturaer på minside.credicare.no
+47 22 80 70 70
CrediCare AS
Postboks 483 Skøyen
NO-0213 Oslo
Norway
https://credicare.no`;

  useEffect(() => {
    if (isEqual(leapClaimsFromSelection, leapClaimsFromSelectedClaims)) {
      return;
    }

    setLeapClaimsFromSelection(leapClaimsFromSelectedClaims);
    setPaymentInformationText(leapClaimsFromSelectedClaims.map(function (debtorClaim) {
      return `${getIdentifierName(debtorClaim.isTransferredToCollection ?? false)} ${debtorClaim.number}
Status: ${getClaimStageName(debtorClaim.claimStage, debtorClaim.caseType!)}
Beløp å betale: ${currencyFormatter.format(debtorClaim.amount!, { code: 'NOK' })}
KID: ${debtorClaim?.kid}
Til konto: ${debtorClaim?.debtorPaymentAccount}${addDueDateIfNotInDebtCollection(debtorClaim)}`;
}).join(`

`));

    props.selectedDebtorClaims.forEach(
      selectedDebtorClaim => (
        leapClaimsFromSelectedClaims.find(c => c.providerSystem === selectedDebtorClaim.providerSystem && c.externalReferenceId === selectedDebtorClaim.externalReferenceId && c.canSendPaymentInformation) != null
          ? null
          : props.toggleDebtorClaimSelection({
            providerSystem: selectedDebtorClaim.providerSystem,
            number: selectedDebtorClaim.number!.toString(),
            type: selectedDebtorClaim.type!.toString(),
            externalReferenceId: selectedDebtorClaim.externalReferenceId!
          })
      ));

  }, [leapClaimsFromSelectedClaims, leapClaimsFromSelection, props]);

  const isValidMobileNumbers = () => {
    return mobileNumbers.length > 0;
  }

  const addDueDateIfNotInDebtCollection = (debtorClaim: LeapClaim) => {
    if ((debtorClaim.claimStage ?? DebtorClaimStage.Undefined) in [DebtorClaimStage.Invoice, DebtorClaimStage.Reminder, DebtorClaimStage.DebtCollectionWarning]) {
      return `
Forfall: ${formatDate(debtorClaim?.dueDate)}`;
    }
    return '';
  };

  const getIdentifierName = (transferredToCollection: boolean) => {
    return transferredToCollection ? "Saksnummer:" : "Fakturanummer:";
  }

  const getClaimStageName = (claimStage: DebtorClaimStage | undefined, caseType: DebtCollectionCaseType) => {
    switch (caseType) {
      case DebtCollectionCaseType.Judicial:
        return 'Rettslig inkasso'
      case DebtCollectionCaseType.Minor:
        return 'Purring'
    }

    switch (claimStage) {
      case DebtorClaimStage.Invoice:
        return 'Faktura'
      case DebtorClaimStage.Reminder:
        return 'Purring'
      case DebtorClaimStage.DebtCollectionWarning:
        return 'Inkassovarsel'
      case DebtorClaimStage.DebtCollection:
        return 'Inkasso'
      default:
        return '';
    }
  }

  const isValidEmailAddresses = () => {
    return emailAddresses.length > 0;
  }

  const isValid = () => {
    return (isValidMobileNumbers() || isValidEmailAddresses()) && hasSelectedDebtorClaims;
  }

  const handleChangeOnIntroductionText = event => {
    setIntroductionText(event.target.value);
  }

  const handleChangeOnPaymentInformationText = event => {
    setPaymentInformationText(event.target.value);
  }

  const handleOnMobileNumberAdded = (value: string) => {
    if (!validateCountryCode(value)) {
      props.addNotification({ message: 'Kan ikke sende SMS til dette landet. Land som er støttet: +44 England, +45 Danmark, +46 Sverige, +47 Norge, +48 Polen, +49 Tyskland, +298 Færøyene, +354 Island, +358 Finland.', variant: 'warning' });
    } else {
      setMobileNumbers([...mobileNumbers, formatPhoneNumber(value)!])
    }
  }

  const validateMobileNumber = (value : string) : boolean => {
    if (value.startsWith('+')) {
      return true
    }
    const phoneNumber = parsePhoneNumber(value, { regionCode: 'NO' });
    return phoneNumber.valid && phoneNumber.typeIsMobile;
  }

  const handleOnMobileNumberDeleted = (value: string) => {
    setMobileNumbers(mobileNumbers.filter((c) => c !== value));
  }

  const handleOnEmailAddressAdded = (value: string) => {
    if (validateEmailAddress(value)) {
      setEmailAddresses([...emailAddresses, value]);
    }
  }

  const handleOnEmailAddressDeleted = (value: string) => {
    setEmailAddresses(emailAddresses.filter((c) => c !== value))
  }

  const handleSubmit = event => {
    event.preventDefault();
    const action: SendPaymentInformationConversationAction = {
      selectedDebtorClaims: selectedDebtorClaims,
      mobilePhoneNumbers: mobileNumbers,
      emailAddresses: emailAddresses,
      additionalText: `${introductionText}

${paymentInformationText}`,
      discriminator: 'SendPaymentInformationConversationAction',
    };

    props.addConversationSupervisorAction(action);
    props.clearDebtorClaimsSelection();
    props.jumpToStep(Step.Summary);
  }

  const handleMobileNumberClick = (number : string) => {
    setMobileNumbers([...mobileNumbers, formatPhoneNumber(number)!]);
  }

  const handleEmailAddressClick = (email: string) => {
    setEmailAddresses([...emailAddresses, email]);
  }

  return (
    <React.Fragment>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        paddingTop: '10px'
      }}>
        <Typography>
          Send betalingsinformasjon
        </Typography>

        {availableMobileNumbers && availableMobileNumbers.map((mobileNumber, i) => (
          <Box key={i} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'end' }}>
            {!mobileNumbers.find(x => x === formatPhoneNumber(mobileNumber)) &&
              <Chip
                data-test-id="mobileNumberSuggestionChip"
                sx={{ marginBottom: '-5px' }}
                label={formatPhoneNumber(mobileNumber)}
                size="small"
                onClick={() => handleMobileNumberClick(mobileNumber)}
                onDelete={() => handleMobileNumberClick(mobileNumber)}
                deleteIcon={<AddCircle color="success" />}
              />
            }
          </Box>
        ))}

        <MuiChipsInput
          disableEdition={true}
          label="Mobilnummer"
          value={mobileNumbers}
          onAddChip={handleOnMobileNumberAdded}
          onDeleteChip={handleOnMobileNumberDeleted}
          sx={{ marginTop: '10px' }}
          validate={validateMobileNumber}
          hideClearAll={true}
          addOnBlur={true}
        />

        {availableEmailAddress &&
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'end' }}>
            {!emailAddresses.find(x => x === availableEmailAddress) &&
              <Chip
                data-test-id="emailAddressSuggestionChip"
                sx={{ marginBottom: '-5px', marginTop: '8px' }}
                label={availableEmailAddress}
                size="small"
                onClick={() => handleEmailAddressClick(availableEmailAddress)}
                onDelete={() => handleEmailAddressClick(availableEmailAddress)}
                deleteIcon={<AddCircle color="success" />}
              />
            }
          </Box>
        }

        <MuiChipsInput
          disableEdition={true}
          label="E-postadresser"
          value={emailAddresses}
          onAddChip={handleOnEmailAddressAdded}
          onDeleteChip={handleOnEmailAddressDeleted}
          sx={{ marginTop: '10px' }}
          hideClearAll={true}
          maxRows={1}
          addOnBlur={true}
        />

        <TextField
          label="Introduksjon"
          margin="normal"
          multiline={true}
          maxRows={3}
          value={introductionText}
          onChange={handleChangeOnIntroductionText}
        />

        <TextField
          label="Betalingsinformasjon"
          margin="normal"
          multiline={true}
          maxRows={8}
          value={paymentInformationText}
          onChange={handleChangeOnPaymentInformationText}
        />

        <TextField
          label="Signatur SMS"
          margin="normal"
          multiline={true}
          maxRows={2}
          value={smsFooter}
          disabled={true}
        />

        <TextField
          label="Signatur e-post"
          margin="normal"
          multiline={true}
          maxRows={4}
          value={emailFooter}
          disabled={true}
        />
      </Box>
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
      }}>
        <Button variant="contained" onClick={() => props.jumpToStep(Step.Start)}>
          Avbryt
        </Button>
        <Button variant="contained" id={'submit'} onClick={handleSubmit} disabled={!isValid()} type="submit" sx={{ marginLeft: '8px' }}>
          OK
        </Button>
      </Box>
    </React.Fragment>
  )
};

const mapStateToProps = ({
  debtorClaims,
  debtorLeapClaims,
  debtorOverview
}: ApplicationState): PropsFromState => ({
  selectedDebtorClaims: debtorClaims.selectedDebtorClaims,
  hasSelectedDebtorClaims: hasSelectedDebtorClaims(debtorClaims),
  leapClaimsFromSelectedClaims: getTopLevelLeapClaimsFromSelectedClaims(
    debtorClaims,
    debtorLeapClaims
  ),
  debtorIndex: debtorOverview.debtor
});

const mapDispatchToProps: PropsFromDispatch = {
  addConversationSupervisorAction:
    ConversationSupervisorStoreActionCreators.addConversationAction,
  clearDebtorClaimsSelection:
    DebtorClaimsStoreActionCreators.clearDebtorClaimsSelection,
  toggleDebtorClaimSelection:
    DebtorClaimsStoreActionCreators.toggleDebtorClaimSelection,
  addNotification: NotificationStoreActionCreators.addNotification,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  SendSelectedInvoices
);
